/**
 * Build Phase Page
 * Implementation and development phase for SQL Server transformations
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Code, GitBranch, Database, CheckCircle, Clock, AlertTriangle, Settings, Users, Activity } from 'lucide-react';

export default function BuildPage() {
  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="border-b border-gray-200 pb-6 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 leading-tight">Build Phase</h1>
              <Badge variant="outline" className="text-yellow-700 bg-yellow-50 border-yellow-200">
                <Clock className="w-3 h-3 mr-1" />
                Pending
              </Badge>
            </div>
            <p className="text-gray-600 max-w-2xl leading-relaxed">
              Implementation and development of SQL Server solutions, code development, and system integration
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="flex items-center gap-2">
              <GitBranch className="w-4 h-4" />
              Version Control
            </Button>
            <Button className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Start Development
            </Button>
          </div>
        </div>
      </div>

      {/* Build Phase Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Code className="w-5 h-5 text-shiftleft-600" />
              Development Streams
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">SQL Schema Migration</span>
                  <Badge variant="secondary">Ready</Badge>
                </div>
                <p className="text-xs text-gray-600">Database structure and schema updates</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Stored Procedures</span>
                  <Badge variant="secondary">Planned</Badge>
                </div>
                <p className="text-xs text-gray-600">Business logic implementation</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">Data Migration Scripts</span>
                  <Badge variant="secondary">Planned</Badge>
                </div>
                <p className="text-xs text-gray-600">ETL and data transformation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Database className="w-5 h-5 text-green-600" />
              Infrastructure Setup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm text-shiftleft-900">Development Environment</span>
                  <Badge className="bg-shiftleft-100 text-shiftleft-800">Setup Required</Badge>
                </div>
                <p className="text-xs text-shiftleft-700">SQL Server 2019 dev instance</p>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm text-yellow-900">Staging Environment</span>
                  <Badge className="bg-yellow-100 text-yellow-800">Planned</Badge>
                </div>
                <p className="text-xs text-yellow-700">Pre-production testing</p>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-sm">CI/CD Pipeline</span>
                  <Badge variant="secondary">Design Phase</Badge>
                </div>
                <p className="text-xs text-gray-600">Automated deployment</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="w-5 h-5 text-purple-600" />
              Team & Resources
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="font-medium text-purple-900 text-sm">Development Team</div>
                <div className="text-xs text-purple-700 mt-1">2 SQL Developers assigned</div>
              </div>

              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="font-medium text-green-900 text-sm">DBA Support</div>
                <div className="text-xs text-green-700 mt-1">Database administration team</div>
              </div>

              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-900 text-sm">Code Review</div>
                <div className="text-xs text-gray-600 mt-1">Senior architect oversight</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Development Roadmap */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-orange-600" />
            Development Roadmap - Sprint Planning
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-4 bg-shiftleft-50 border border-shiftleft-200 rounded-lg">
                <div className="font-medium text-shiftleft-900 mb-2">Sprint 1 - Foundation</div>
                <div className="text-sm text-shiftleft-700 space-y-1">
                  <div>• Environment setup</div>
                  <div>• Core schema migration</div>
                  <div>• Basic stored procedures</div>
                </div>
                <Badge className="mt-2 bg-shiftleft-100 text-shiftleft-800">2 weeks</Badge>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="font-medium text-yellow-900 mb-2">Sprint 2 - Core Logic</div>
                <div className="text-sm text-yellow-700 space-y-1">
                  <div>• Business logic implementation</div>
                  <div>• Data validation rules</div>
                  <div>• Error handling</div>
                </div>
                <Badge className="mt-2 bg-yellow-100 text-yellow-800">3 weeks</Badge>
              </div>

              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="font-medium text-purple-900 mb-2">Sprint 3 - Integration</div>
                <div className="text-sm text-purple-700 space-y-1">
                  <div>• API integrations</div>
                  <div>• External system connections</div>
                  <div>• Performance optimization</div>
                </div>
                <Badge className="mt-2 bg-purple-100 text-purple-800">2 weeks</Badge>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="font-medium text-green-900 mb-2">Sprint 4 - Finalization</div>
                <div className="text-sm text-green-700 space-y-1">
                  <div>• Code review & cleanup</div>
                  <div>• Documentation</div>
                  <div>• Deployment preparation</div>
                </div>
                <Badge className="mt-2 bg-green-100 text-green-800">1 week</Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Next Steps */}
      <Card className="bg-shiftleft-50 border-shiftleft-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-shiftleft-900">
            <CheckCircle className="w-5 h-5" />
            Next Steps
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-shiftleft-900 mb-2">Prerequisites</h4>
              <ul className="text-sm text-shiftleft-700 space-y-1">
                <li>• Complete design phase documentation</li>
                <li>• Environment provisioning approval</li>
                <li>• Development team onboarding</li>
                <li>• Version control setup</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-shiftleft-900 mb-2">Immediate Actions</h4>
              <ul className="text-sm text-shiftleft-700 space-y-1">
                <li>• Schedule development kickoff meeting</li>
                <li>• Finalize sprint planning</li>
                <li>• Setup development environments</li>
                <li>• Create project repositories</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
